import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  FaArrowLeft,
  FaArrowRight,
  FaBookOpen,
  FaCheckCircle,
  FaCreditCard,
  FaCrown,
  FaExclamationCircle,
  FaGlobe,
  FaLock,
  FaMoneyCheckAlt,
  FaPaypal,
  FaRedo,
  FaShieldAlt,
  FaSignOutAlt,
  FaUniversity,
} from "react-icons/fa";

import { useAuth } from "../AuthContext";
import Login from "../Login/Login";
import {
  purchaseWebsitePremium,
  restoreWebsitePremiumAccess,
} from "../services/revenueCatWeb";

import "./SubscriptionMonthly.css";

type PaymentMethodKey = "card" | "paypal" | "sepa" | "bank";

type SubscriptionMonthlyProps = {
  isOpen: boolean;
  onClose: () => void;
  onSubscribed: () => void;
};

type PaymentMethod = {
  key: PaymentMethodKey;
  title: string;
  subtitle: string;
  badge?: string;
  icon: React.ReactNode;
};

const PAYMENT_METHODS: PaymentMethod[] = [
  {
    key: "card",
    title: "Karte",
    subtitle: "Visa / Mastercard",
    badge: "Meist genutzt",
    icon: <FaCreditCard />,
  },
  {
    key: "paypal",
    title: "PayPal",
    subtitle: "Schnell bezahlen",
    icon: <FaPaypal />,
  },
  {
    key: "sepa",
    title: "SEPA",
    subtitle: "Lastschrift",
    icon: <FaUniversity />,
  },
  {
    key: "bank",
    title: "Überweisung",
    subtitle: "Bankzahlung",
    icon: <FaMoneyCheckAlt />,
  },
];

const getCleanPaymentError = (error: unknown): string => {
  const rawMessage =
    error instanceof Error
      ? error.message
      : typeof error === "string"
        ? error
        : "";

  const message = rawMessage.trim();
  const lowerMessage = message.toLowerCase();

  if (!message) {
    return "Die Zahlung konnte nicht gestartet werden. Bitte versuchen Sie es erneut.";
  }

  if (
    lowerMessage.includes("cancel") ||
    lowerMessage.includes("cancelled") ||
    lowerMessage.includes("canceled") ||
    lowerMessage.includes("abgebrochen")
  ) {
    return "Die Zahlung wurde abgebrochen.";
  }

  if (
    lowerMessage.includes("invalid api key") ||
    lowerMessage.includes("web billing api key") ||
    lowerMessage.includes("api-key") ||
    lowerMessage.includes("api key")
  ) {
    return "Die Zahlung konnte nicht gestartet werden. Bitte prüfen Sie den Web-Billing-API-Schlüssel.";
  }

  if (
    lowerMessage.includes("produkt wurde nicht gefunden") ||
    lowerMessage.includes("package") ||
    lowerMessage.includes("offering") ||
    lowerMessage.includes("website_full_month")
  ) {
    return "Das Monatsabo wurde nicht gefunden. Bitte prüfen Sie das Angebot und das Produkt im Zahlungsbereich.";
  }

  if (
    lowerMessage.includes("revenuecat") ||
    lowerMessage.includes("stripe") ||
    lowerMessage.includes("payment")
  ) {
    return "Die Zahlung konnte nicht gestartet werden. Bitte prüfen Sie die Zahlungseinstellungen.";
  }

  return message;
};

const SubscriptionMonthly: React.FC<SubscriptionMonthlyProps> = ({
  isOpen,
  onClose,
  onSubscribed,
}) => {
  const { userId, userEmail, isLoggedIn, authLoading, logout } = useAuth();

  const isLoggedInRef = useRef<boolean>(isLoggedIn);
  const userIdRef = useRef<string | null | undefined>(userId);

  const [selectedPaymentMethod, setSelectedPaymentMethod] =
    useState<PaymentMethodKey>("card");
  const [loading, setLoading] = useState<boolean>(false);
  const [restoreLoading, setRestoreLoading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [successMessage, setSuccessMessage] = useState<string>("");

  const price = process.env.REACT_APP_WEBSITE_FULL_PRICE_EUR?.trim() || "4.99";

  useEffect(() => {
    isLoggedInRef.current = isLoggedIn;
    userIdRef.current = userId;
  }, [isLoggedIn, userId]);

  useEffect(() => {
    if (!isOpen) {
      setSelectedPaymentMethod("card");
      setLoading(false);
      setRestoreLoading(false);
      setErrorMessage("");
      setSuccessMessage("");
    }
  }, [isOpen]);

  const formattedPrice = useMemo(() => {
    const cleanPrice = Number(price.replace(",", "."));

    if (Number.isFinite(cleanPrice)) {
      return new Intl.NumberFormat("de-DE", {
        style: "currency",
        currency: "EUR",
        minimumFractionDigits: cleanPrice % 1 === 0 ? 0 : 2,
        maximumFractionDigits: cleanPrice % 1 === 0 ? 0 : 2,
      }).format(cleanPrice);
    }

    return `${price.replace(".", ",")} €`;
  }, [price]);

  const selectedPaymentTitle = useMemo(() => {
    return (
      PAYMENT_METHODS.find((method) => method.key === selectedPaymentMethod)
        ?.title ?? "Karte"
    );
  }, [selectedPaymentMethod]);

  if (!isOpen) {
    return null;
  }

  const resetMessages = (): void => {
    setErrorMessage("");
    setSuccessMessage("");
  };

  const handleClose = (): void => {
    resetMessages();
    onClose();
  };

  const handleLoginClose = (): void => {
    resetMessages();

    window.setTimeout(() => {
      if (isLoggedInRef.current && userIdRef.current) {
        return;
      }

      onClose();
    }, 450);
  };

  const handlePurchase = async (): Promise<void> => {
    resetMessages();

    if (authLoading || loading) {
      return;
    }

    if (!isLoggedIn || !userId) {
      setErrorMessage("Bitte loggen Sie sich zuerst ein.");
      return;
    }

    setLoading(true);

    try {
      const hasAccess = await purchaseWebsitePremium({
        appUserId: userId,
        email: userEmail,
      });

      if (!hasAccess) {
        setErrorMessage(
          "Die Zahlung wurde geöffnet, aber der Zugang wurde noch nicht erkannt. Bitte klicken Sie auf „Zugang prüfen“.",
        );
        return;
      }

      setSuccessMessage(
        "Premium-Zugang ist aktiv. Alle Deutschbücher sind freigeschaltet.",
      );
      onSubscribed();
    } catch (error) {
      setErrorMessage(getCleanPaymentError(error));
    } finally {
      setLoading(false);
    }
  };

  const handleRestore = async (): Promise<void> => {
    resetMessages();

    if (authLoading || restoreLoading) {
      return;
    }

    if (!isLoggedIn || !userId) {
      setErrorMessage("Bitte loggen Sie sich zuerst ein.");
      return;
    }

    setRestoreLoading(true);

    try {
      const hasAccess = await restoreWebsitePremiumAccess(userId, userEmail);

      if (hasAccess) {
        setSuccessMessage(
          "Premium-Zugang wurde gefunden. Alle Deutschbücher sind freigeschaltet.",
        );
        onSubscribed();
        return;
      }

      setErrorMessage(
        "Für dieses Konto wurde noch kein aktives Monatsabo gefunden.",
      );
    } catch (error) {
      setErrorMessage(getCleanPaymentError(error));
    } finally {
      setRestoreLoading(false);
    }
  };

  const handleLogout = async (): Promise<void> => {
    resetMessages();
    await logout();
  };

  if (authLoading) {
    return (
      <div className="subscription-monthly-backdrop" role="presentation">
        <article className="subscription-monthly-card subscription-monthly-loading-card">
          <div className="subscription-monthly-particles" aria-hidden="true">
            <span />
            <span />
            <span />
            <span />
          </div>

          <div className="subscription-monthly-badge">
            <FaShieldAlt />
            <span>Bitte warten</span>
          </div>

          <h1>Premium-Zugang wird geprüft</h1>
          <p>Bitte warten Sie kurz. Ihr Konto wird sicher geprüft.</p>
        </article>
      </div>
    );
  }

  if (!isLoggedIn || !userId) {
    return <Login isOpen={isOpen} onClose={handleLoginClose} />;
  }

  return (
    <div
      className="subscription-monthly-backdrop"
      role="dialog"
      aria-modal="true"
      aria-labelledby="subscription-monthly-title"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) {
          handleClose();
        }
      }}
    >
      <section className="subscription-monthly-card">
        <div className="subscription-monthly-particles" aria-hidden="true">
          <span />
          <span />
          <span />
          <span />
        </div>

        <button
          type="button"
          className="subscription-monthly-back"
          onClick={handleClose}
          aria-label="Schließen"
        >
          <FaArrowLeft />
        </button>

        <div className="subscription-monthly-top-row">
          <div className="subscription-monthly-badge">
            <FaCrown />
            <span>Premium-Zugang</span>
          </div>
        </div>

        <div className="subscription-monthly-title-row">
          <div>
            <h1 id="subscription-monthly-title">Monatsabo abschließen</h1>
            <p>
              Schalten Sie alle Deutschbücher von A1 bis C2 frei. Weitere
              Premium-Inhalte der Webseite werden ebenfalls über dieses Abo
              freigeschaltet.
            </p>
          </div>

          <strong>{formattedPrice}</strong>
        </div>

        {errorMessage && (
          <div className="subscription-monthly-error">
            <FaExclamationCircle />
            <span>{errorMessage}</span>
          </div>
        )}

        {successMessage && (
          <div className="subscription-monthly-success">
            <FaCheckCircle />
            <span>{successMessage}</span>
          </div>
        )}

        <div className="subscription-monthly-benefits">
          <div>
            <FaBookOpen />
            <span>60 PDF-Bücher</span>
          </div>

          <div>
            <FaGlobe />
            <span>Alle Stufen A1 bis C2</span>
          </div>

          <div>
            <FaShieldAlt />
            <span>Sichere Zahlung</span>
          </div>

          <div>
            <FaLock />
            <span>Premium-Inhalte inklusive</span>
          </div>
        </div>

        <div className="subscription-monthly-method-box">
          <div className="subscription-monthly-method-heading">
            <span>Zahlungsart</span>
            <strong>Standard: {selectedPaymentTitle}</strong>
          </div>

          <div className="subscription-monthly-method-grid">
            {PAYMENT_METHODS.map((method) => (
              <button
                key={method.key}
                type="button"
                className={`subscription-monthly-method ${
                  selectedPaymentMethod === method.key
                    ? "subscription-monthly-method-active"
                    : ""
                }`}
                onClick={() => setSelectedPaymentMethod(method.key)}
              >
                <span className="subscription-monthly-method-icon">
                  {method.icon}
                </span>

                <span className="subscription-monthly-method-text">
                  <strong>{method.title}</strong>
                  <small>{method.subtitle}</small>
                </span>

                {method.badge && (
                  <span className="subscription-monthly-method-badge">
                    {method.badge}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>

        <div className="subscription-monthly-summary">
          <div>
            <span>Abo</span>
            <strong>Premium-Zugang</strong>
          </div>

          <div>
            <span>Laufzeit</span>
            <strong>Monatlich</strong>
          </div>

          <div>
            <span>Preis</span>
            <strong>{formattedPrice}</strong>
          </div>

          <div>
            <span>Konto</span>
            <strong>{userEmail || "Angemeldet"}</strong>
          </div>
        </div>

        <div className="subscription-monthly-actions">
          <button
            type="button"
            className="subscription-monthly-main-button"
            onClick={handlePurchase}
            disabled={loading || restoreLoading}
          >
            {loading ? (
              <span>Zahlung wird geöffnet...</span>
            ) : (
              <>
                <span>Monatsabo starten</span>
                <FaArrowRight />
              </>
            )}
          </button>

          <button
            type="button"
            className="subscription-monthly-secondary-button"
            onClick={handleRestore}
            disabled={loading || restoreLoading}
          >
            {restoreLoading ? (
              <span>Zugang wird geprüft...</span>
            ) : (
              <>
                <FaRedo />
                <span>Zugang prüfen</span>
              </>
            )}
          </button>

          <button
            type="button"
            className="subscription-monthly-secondary-button"
            onClick={handleLogout}
            disabled={loading || restoreLoading}
          >
            <FaSignOutAlt />
            <span>Abmelden</span>
          </button>
        </div>
      </section> 
    </div>
  );
};

export default SubscriptionMonthly; 